import { useCallback, useEffect, useRef, useState, type MouseEvent, type MouseEventHandler } from 'react';
import { resolvePressFeedbackDelayMs } from './pressFeedback.js';

type Options = {
  disabled?: boolean;
  /**
   * Polymorphic `as={Link}` / host that owns navigation.
   * Kept for call-site compatibility; action is never deferred (native click runs as usual).
   */
  deferHostDefault?: boolean;
  /**
   * Native `<button type="submit">`. Kept for call-site compatibility; with sync
   * handlers the browser default submits the form (no delayed requestSubmit).
   */
  submitForm?: boolean;
};

/**
 * Runs the click action immediately. Keeps press fill visible briefly after pointer-up
 * via `data-wp-pressing` (`:active` ends before `click`).
 */
export function usePressFeedbackClick<E extends Element>(
  onClick: MouseEventHandler<E> | undefined,
  options: Options = {},
): {
  onClick: MouseEventHandler<E>;
  pressing: boolean;
  /** Spread onto the pressed element for CSS `:is(:active, [data-wp-pressing])`. */
  pressAttrs: { 'data-wp-pressing'?: '' };
} {
  const { disabled = false } = options;
  const [pressing, setPressing] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;

  useEffect(() => {
    return () => {
      if (timerRef.current != null) clearTimeout(timerRef.current);
    };
  }, []);

  const handleClick = useCallback(
    (event: MouseEvent<E>) => {
      if (disabled) return;

      const holdMs = resolvePressFeedbackDelayMs();
      if (holdMs > 0) {
        if (timerRef.current != null) clearTimeout(timerRef.current);
        setPressing(true);
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          setPressing(false);
        }, holdMs);
      }

      onClickRef.current?.(event);
    },
    [disabled],
  );

  return {
    onClick: handleClick,
    pressing,
    pressAttrs: pressing ? { 'data-wp-pressing': '' } : {},
  };
}
